import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Users,
    Search,
    Mail,
    Phone,
    Eye,
    X,
    TrendingUp,
    UserCheck,
    Building2,
    BookOpen,
    GraduationCap,
    UserCog,
    Star,
    Download,
    AlertTriangle,
    AlertCircle,
    CheckCircle,
    Pencil,
} from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import RateHistoryDialog, { RatePerson } from '../components/payroll/RateHistoryDialog';
import { describePayRate, setPayRates } from '../utils/payRates';
import PageContainer from '../components/PageContainer';
import {
    Button,
    DataToolbar,
    EmptyState,
    Input,
    LocalPagination,
    LoadingState,
    Modal,
    ResponsiveDataView,
    SectionPanel,
    StatCard,
    StatusBadge,
    useLocalPagination,
} from '../components/ui';
import { openOnRowClick } from '../utils/rowClick';
import { matchesQuery } from '../utils/search';

const TEACHERS_PAGE_SIZE = 10;

const Teachers = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    // Pay rates are admin-only; the API leaves them out for everyone else.
    const isAdmin = user?.role === 'ADMIN';
    const [ratePerson, setRatePerson] = useState<RatePerson | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedRole, setSelectedRole] = useState('all');
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedTeacher, setSelectedTeacher] = useState<any>(null);
    const [teachers, setTeachers] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState<any>(null);

    useEffect(() => {
        fetchTeachers();
        fetchStats();
    }, []);

    const fetchTeachers = async () => {
        try {
            setLoading(true);
            const response = await api.get('/teachers');
            if (response.data.success) {
                setTeachers(response.data.data);
            } else {
                console.error('Failed to fetch teachers:', response.data.message);
            }
        } catch (error) {
            console.error('Error fetching teachers:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await api.get('/teachers/stats/overview');
            if (response.data.success) {
                setStats(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    const handleViewTeacher = async (teacher: any) => {
        try {
            const response = await api.get(`/teachers/${teacher.id}`);
            if (response.data.success) {
                setSelectedTeacher(response.data.data);
                setShowViewModal(true);
            }
        } catch (error) {
            console.error('Error fetching teacher details:', error);
        }
    };

    // Exports live on "Dữ liệu & báo cáo" (staff lists are for admins and managers).
    const handleExportData = () => navigate('/reports?report=staff');

    const filteredTeachers = teachers.filter(teacher => {
        const matchesSearch = matchesQuery([teacher.fullName, teacher.email, teacher.phone].filter(Boolean).join(' '), searchTerm);
        const matchesRole = selectedRole === 'all' || teacher.role === selectedRole;

        return matchesSearch && matchesRole;
    });
    const {
        page: teachersPage,
        pageSize: teachersPageSize,
        pagedItems: pagedTeachers,
        setPage: setTeachersPage,
        setPageSize: setTeachersPageSize,
    } = useLocalPagination(
        filteredTeachers,
        TEACHERS_PAGE_SIZE,
        `${searchTerm}|${selectedRole}`,
    );

    const getRoleColor = (role: string) => {
        switch (role) {
            case 'TEACHER': return 'bg-academy-50 text-academy-800';
            case 'ASSISTANT': return 'bg-success-100 text-success-800';
            default: return 'bg-ink-100 text-ink-800';
        }
    };

    const getRoleIcon = (role: string) => {
        switch (role) {
            case 'TEACHER': return <GraduationCap className="w-4 h-4" />;
            case 'ASSISTANT': return <UserCog className="w-4 h-4" />;
            default: return <Users className="w-4 h-4" />;
        }
    };

    const getStatusColor = (isActive: boolean) => {
        return isActive ? 'text-success-600' : 'text-danger-600';
    };

    const getStatusIcon = (isActive: boolean) => {
        return isActive ?
            <CheckCircle className="w-4 h-4" /> :
            <AlertCircle className="w-4 h-4" />;
    };

    const getRoleTone = (role: string): 'success' | 'warning' | 'danger' | 'neutral' | 'info' => {
        switch (role) {
            case 'TEACHER': return 'info';
            case 'ASSISTANT': return 'success';
            default: return 'neutral';
        }
    };

    const renderRoleBadge = (role: string) => (
        <StatusBadge tone={getRoleTone(role)} icon={getRoleIcon(role)}>
            {role === 'TEACHER' ? 'Giáo viên' : 'Trợ giảng'}
        </StatusBadge>
    );

    const renderStatusBadge = (isActive: boolean) => (
        <StatusBadge tone={isActive ? 'success' : 'neutral'} icon={getStatusIcon(isActive)}>
            {isActive ? 'Hoạt động' : 'Ngưng hoạt động'}
        </StatusBadge>
    );

    return (
        <PageContainer
            title="Giáo viên & Trợ giảng"
            subtitle={`${teachers.length} người đang dạy và trợ giảng ở trung tâm.`}
            actions={user?.role === 'ADMIN' || user?.role === 'MANAGER' ? (
                <Button
                    variant="secondary"
                    icon={<Download className="h-4 w-4" />}
                    onClick={handleExportData}
                >
                    Xuất dữ liệu
                </Button>
            ) : undefined}
        >

            {/* Stats Cards */}
            {stats && (
                <div className="grid gap-px overflow-hidden rounded-lg border border-ink-200 bg-ink-200 grid-cols-2 xl:grid-cols-4">
                    <StatCard
                        label="Giáo viên"
                        value={stats.teachers.total}
                        detail={`${stats.teachers.active} hoạt động`}
                        icon={<GraduationCap className="h-5 w-5" />}
                        tone="academy"
                    />
                    <StatCard
                        label="Trợ giảng"
                        value={stats.assistants.total}
                        detail={`${stats.assistants.active} hoạt động`}
                        icon={<UserCog className="h-5 w-5" />}
                        tone="action"
                    />
                    <StatCard
                        label="Tổng nhân sự"
                        value={stats.overall.total}
                        detail={`${stats.overall.active} đang hoạt động`}
                        icon={<Users className="h-5 w-5" />}
                        tone="academy"
                    />

                    {/* <div className="bg-white p-4 rounded-lg shadow-sm border">
                        <div className="flex items-center">
                            <div className="p-2 bg-academy-100 rounded-lg">
                                <BookOpen className="w-5 h-5 text-academy-600" />
                            </div>
                            <div className="ml-3">
                                <p className="text-sm font-medium text-ink-600">Lớp học</p>
                                <p className="text-xl font-bold text-ink-900">{stats.classes?.total || 0}</p>
                                <p className="text-xs text-ink-500">Tổng số</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white p-4 rounded-lg shadow-sm border">
                        <div className="flex items-center">
                            <div className="p-2 bg-academy-100 rounded-lg">
                                <UserCheck className="w-5 h-5 text-academy-600" />
                            </div>
                            <div className="ml-3">
                                <p className="text-sm font-medium text-ink-600">Học sinh</p>
                                <p className="text-xl font-bold text-ink-900">{stats.students?.total || 0}</p>
                                <p className="text-xs text-ink-500">Đang học</p>
                            </div>
                        </div>
                    </div> */}

                    <StatCard
                        label="Tỷ lệ hoạt động"
                        value={`${stats.overall.total > 0 ? Math.round((stats.overall.active / stats.overall.total) * 100) : 0}%`}
                        detail="Hiện tại"
                        icon={<TrendingUp className="h-5 w-5" />}
                        tone="warning"
                    />
                </div>
            )}

            {/* Filters */}
            <DataToolbar
                className="sm:items-center"
                controlsLayoutClassName="grid flex-1 gap-2 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-center"
            >
                <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Tìm tên hoặc email…"
                    leftIcon={<Search className="h-4 w-4" />}
                    aria-label="Tìm kiếm giáo viên"
                    className="min-h-9 lg:w-72"
                />
                <select
                    className="min-h-9 w-full rounded-lg border border-ink-300 bg-white px-3 text-sm text-ink-900 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-100 lg:w-44"
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    aria-label="Lọc theo vai trò"
                >
                    <option value="all">Mọi vai trò</option>
                    <option value="TEACHER">Giáo viên</option>
                    <option value="ASSISTANT">Trợ giảng</option>
                </select>
            </DataToolbar>

            {/* Teachers Table */}
            <SectionPanel className="overflow-hidden max-lg:rounded-none max-lg:border-0 max-lg:bg-transparent max-lg:shadow-none">

                {loading ? (
                    <LoadingState title="Đang tải danh sách giáo viên..." />
                ) : filteredTeachers.length === 0 ? (
                    <EmptyState
                        title="Không có dữ liệu"
                        description={searchTerm || selectedRole !== 'all'
                            ? 'Không tìm thấy giáo viên/trợ giảng phù hợp với bộ lọc.'
                            : 'Chưa có giáo viên hoặc trợ giảng nào.'
                        }
                    />
                ) : (
                    <>
                    <ResponsiveDataView
                        desktopLabel="Bảng giáo viên và trợ giảng"
                        mobileLabel="Danh sách giáo viên và trợ giảng"
                        desktop={(
                                <table className="min-w-full divide-y divide-ink-200 text-sm">
                                    <thead>
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-ink-500">
                                                Thông tin
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-ink-500">
                                                Vai trò
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-ink-500">
                                                Trạng thái
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-ink-500">
                                                Thông tin liên hệ
                                            </th>
                                            {isAdmin ? (
                                                <th className="w-12 px-3 py-3"><span className="sr-only">Lương</span></th>
                                            ) : null}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-ink-200 bg-white">
                                        {pagedTeachers.map((teacher) => (
                                            <tr
                                                key={teacher.id}
                                                onClick={isAdmin ? openOnRowClick(() => handleViewTeacher(teacher)) : undefined}
                                                className={isAdmin ? 'cursor-pointer hover:bg-ink-50' : undefined}
                                            >
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <div className="h-10 w-10 flex-shrink-0">
                                                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-ink-100">
                                                                <span className="text-sm font-semibold text-ink-700">
                                                                    {teacher.firstName.charAt(0)}{teacher.lastName.charAt(0)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="ml-4">
                                                            {isAdmin ? (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleViewTeacher(teacher)}
                                                                    aria-label={`Xem chi tiết ${teacher.fullName}`}
                                                                    className="cursor-pointer text-left text-sm font-medium text-ink-900 hover:text-academy-600"
                                                                >
                                                                    {teacher.fullName}
                                                                </button>
                                                            ) : (
                                                                <div className="text-sm font-medium text-ink-900">{teacher.fullName}</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {renderRoleBadge(teacher.role)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {renderStatusBadge(teacher.isActive)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-700">
                                                    <div className="space-y-1">
                                                        <div className="flex items-center text-ink-700">
                                                            <Phone className="mr-1 h-3 w-3 text-ink-400" />
                                                            <span>{teacher.phone || 'Chưa có số điện thoại'}</span>
                                                        </div>
                                                        <div className="flex items-center text-ink-500">
                                                            <Mail className="mr-1 h-3 w-3 text-ink-400" />
                                                            <span>{teacher.email}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                {isAdmin ? (
                                                    <td className="px-3 py-4 whitespace-nowrap text-center">
                                                        <div className="flex items-center justify-center gap-2">
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={() => setRatePerson(teacher)}
                                                                icon={<Pencil className="h-4 w-4" />}
                                                                aria-label={`Chỉnh sửa lương ${teacher.fullName}`}
                                                                title="Chỉnh sửa lương theo giờ"
                                                            />
                                                        </div>
                                                    </td>
                                                ) : null}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                        )}
                        mobile={(
                            <div className="grid gap-3">
                                {pagedTeachers.map((teacher) => (
                                    <article
                                        key={teacher.id}
                                        onClick={isAdmin ? openOnRowClick(() => handleViewTeacher(teacher)) : undefined}
                                        className={`rounded-lg border border-ink-200 bg-white p-4${isAdmin ? ' cursor-pointer transition-colors hover:bg-ink-50' : ''}`}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-center space-x-3">
                                                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-ink-100">
                                                    <span className="text-lg font-semibold text-ink-700">
                                                        {teacher.firstName.charAt(0)}{teacher.lastName.charAt(0)}
                                                    </span>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="truncate text-base font-semibold text-ink-900">
                                                        {isAdmin ? (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleViewTeacher(teacher)}
                                                                aria-label={`Xem chi tiết ${teacher.fullName}`}
                                                                className="max-w-full cursor-pointer truncate text-left"
                                                            >
                                                                {teacher.fullName}
                                                            </button>
                                                        ) : teacher.fullName}
                                                    </h3>
                                                    <div className="mt-1 flex items-center text-sm text-ink-500">
                                                        <Mail className="w-4 h-4 mr-1" />
                                                        <span className="truncate">{teacher.email}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mt-4 space-y-3">
                                            <div className="flex items-center justify-between">
                                                {renderRoleBadge(teacher.role)}
                                                {renderStatusBadge(teacher.isActive)}
                                            </div>

                                            <div className="space-y-2 border-t border-ink-200 pt-2">
                                                <div className="flex items-center text-sm text-ink-700">
                                                    <Phone className="w-4 h-4 mr-2 text-ink-400" />
                                                    <span className="truncate">{teacher.phone || 'Chưa có số điện thoại'}</span>
                                                </div>
                                                <div className="flex items-center text-sm text-ink-500">
                                                    <Mail className="w-4 h-4 mr-2 text-ink-400" />
                                                    <span className="truncate">{teacher.email}</span>
                                                </div>
                                                {/* Details and pay are admin-only; everyone else sees the contact card. */}
                                                {isAdmin ? (
                                                    <div className="flex flex-wrap gap-2">
                                                        <Button
                                                            variant="secondary"
                                                            size="sm"
                                                            onClick={() => setRatePerson(teacher)}
                                                            icon={<Pencil className="h-4 w-4" />}
                                                            aria-label={`Chỉnh sửa lương ${teacher.fullName}`}
                                                        >
                                                            Lương theo ca
                                                        </Button>
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        )}
                    />
                    <LocalPagination
                        label="danh sách giáo viên và trợ giảng"
                        page={teachersPage}
                        pageSize={teachersPageSize}
                        totalItems={filteredTeachers.length}
                        pageSizeOptions={[10, 20, 50]}
                        onPageChange={setTeachersPage}
                        onPageSizeChange={setTeachersPageSize}
                    />
                    </>
                )}
            </SectionPanel>

            {selectedTeacher && (
                <Modal
                    open={showViewModal}
                    title="Chi tiết giáo viên/trợ giảng"
                    description="Thông tin chi tiết và thống kê"
                    onClose={() => setShowViewModal(false)}
                    className="lg:max-w-4xl"
                    footer={(
                        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <Button variant="secondary" onClick={() => setShowViewModal(false)}>
                                Đóng
                            </Button>
                            {isAdmin ? (
                                <Button icon={<Pencil className="h-4 w-4" />} onClick={() => setRatePerson(selectedTeacher)}>
                                    Chỉnh sửa lương
                                </Button>
                            ) : null}
                        </div>
                    )}
                >
                        <div className="space-y-6">
                            {/* Basic Info */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-ink-700 mb-1">Họ và tên</label>
                                        <p className="text-sm text-ink-900">{selectedTeacher.fullName}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-ink-700 mb-1">Email</label>
                                        <p className="text-sm text-ink-900">{selectedTeacher.email}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-ink-700 mb-1">Số điện thoại</label>
                                        <p className="text-sm text-ink-900">{selectedTeacher.phone || 'Chưa có số điện thoại'}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-ink-700 mb-1">Vai trò</label>
                                        {renderRoleBadge(selectedTeacher.role)}
                                    </div>
                                    {isAdmin ? (
                                        <div>
                                            <label className="block text-sm font-medium text-ink-700 mb-1">Lương theo ca hiện tại</label>
                                            {setPayRates(selectedTeacher.payRates).length === 0 ? (
                                                <p className="text-sm text-ink-900">Chưa có</p>
                                            ) : setPayRates(selectedTeacher.payRates).map(item => (
                                                <p key={item.shiftTypeId} className="text-sm text-ink-900">
                                                    {item.name}: {describePayRate(item)}
                                                </p>
                                            ))}
                                        </div>
                                    ) : null}
                                    <div>
                                        <label className="block text-sm font-medium text-ink-700 mb-1">Trạng thái</label>
                                        {renderStatusBadge(selectedTeacher.isActive)}
                                    </div>
                                </div>

                                {/* Statistics */}
                                <div className="space-y-4">
                                    <h4 className="text-sm font-medium text-ink-700">Thống kê</h4>
                                    <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-ink-200 bg-ink-200">
                                        <div className="bg-white px-3 py-2.5">
                                            <div className="text-lg font-semibold tabular-nums text-ink-900">{selectedTeacher.stats.totalClasses}</div>
                                            <div className="text-xs text-ink-500">Tổng lớp</div>
                                        </div>
                                        <div className="bg-white px-3 py-2.5">
                                            <div className="text-lg font-semibold tabular-nums text-ink-900">{selectedTeacher.stats.totalStudents}</div>
                                            <div className="text-xs text-ink-500">Tổng học sinh</div>
                                        </div>
                                        <div className="bg-white px-3 py-2.5">
                                            <div className="text-lg font-semibold tabular-nums text-ink-900">{selectedTeacher.stats.teachingClasses}</div>
                                            <div className="text-xs text-ink-500">Lớp dạy</div>
                                        </div>
                                        <div className="bg-white px-3 py-2.5">
                                            <div className="text-lg font-semibold tabular-nums text-ink-900">{selectedTeacher.stats.assistingClasses}</div>
                                            <div className="text-xs text-ink-500">Lớp trợ giảng</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Classes */}
                            {selectedTeacher.classes && selectedTeacher.classes.length > 0 && (
                                <div>
                                    <h4 className="text-lg font-medium text-ink-900 mb-4">Lớp học ({selectedTeacher.classes.length})</h4>
                                    <div className="space-y-3">
                                        {selectedTeacher.classes.map((classInfo: any, index: number) => (
                                            <div key={index} className="p-4 border border-ink-200 rounded-lg">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex-1">
                                                        <div className="flex items-center space-x-2">
                                                            <h5 className="font-medium text-ink-900">{classInfo.name}</h5>
                                                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${classInfo.status === 'active' ? 'bg-success-100 text-success-800' :
                                                                classInfo.status === 'full' ? 'bg-warning-100 text-warning-800' :
                                                                    'bg-ink-100 text-ink-800'
                                                                }`}>
                                                                {classInfo.status === 'active' ? 'Đang học' :
                                                                    classInfo.status === 'full' ? 'Đầy lớp' :
                                                                        classInfo.status || 'N/A'}
                                                            </span>
                                                        </div>
                                                        <div className="mt-1 flex items-center space-x-4 text-sm text-ink-500">
                                                            <div className="flex items-center">
                                                                <BookOpen className="w-4 h-4 mr-1" />
                                                                {classInfo.subject || 'N/A'}
                                                            </div>
                                                            <div className="flex items-center">
                                                                <Building2 className="w-4 h-4 mr-1" />
                                                                {classInfo.campus || 'N/A'}
                                                            </div>
                                                            <div className="flex items-center">
                                                                <Users className="w-4 h-4 mr-1" />
                                                                {classInfo.enrolledStudents || 0}/{classInfo.capacity || 0} học sinh
                                                            </div>
                                                        </div>
                                                        <div className="mt-2 flex items-center space-x-4 text-xs text-ink-400">
                                                            {classInfo.startDate && (
                                                                <span>Từ: {new Date(classInfo.startDate).toLocaleDateString('vi-VN')}</span>
                                                            )}
                                                            {classInfo.endDate && (
                                                                <span>Đến: {new Date(classInfo.endDate).toLocaleDateString('vi-VN')}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                </Modal>
            )}
            <RateHistoryDialog
                person={ratePerson}
                onClose={() => setRatePerson(null)}
                onSaved={rates => {
                    const personId = ratePerson?.id;
                    setTeachers(current => current.map(item => (item.id === personId ? { ...item, ...rates } : item)));
                    setSelectedTeacher((current: any) => (current?.id === personId ? { ...current, ...rates } : current));
                }}
            />
        </PageContainer>
    );
};

export default Teachers;
